import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, Award, Users, Shield, Clock, Star, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, credentials, servicesList, serviceAreas } from '../data/mock';
import { serviceImages, serviceDescriptions } from '../data/serviceData';
import { pageSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';
import HomeBlogCards from '../components/HomeBlogCards';
import TextLink from '../components/TextLink';

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      <SEOHead 
        title={pageSEO.home.title}
        description={pageSEO.home.description}
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/5a35076f-aa67-447c-9eac-34696923d44e/images/7ea633d787fac16cdf9482efc776fff53ba48ed0f841c6a96eca6dc12b513f75.png')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/95 via-neutral-900/80 to-neutral-900/40" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6 animate-fadeInUp">
              <span className="bg-amber-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                55+ Years of Excellence
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fadeInUp animation-delay-100">
              Quality Painting &<br />
              <span className="text-amber-400">Remodeling</span> You Can Trust
            </h1>
            
            <p className="text-lg md:text-xl text-neutral-300 mb-8 animate-fadeInUp animation-delay-200 leading-relaxed">
              Transform your Dallas home with over five decades of trusted craftsmanship. 
              Family-owned, fully licensed, and committed to excellence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-300">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto">
                  Request Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-6 py-6 text-lg w-full sm:w-auto">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Us
                </Button>
              </a>
              <TextLink>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-6 py-6 text-lg w-full sm:w-auto">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Text Us
                </Button>
              </TextLink>
            </div>
            
            <div className="flex flex-wrap gap-6 mt-10 animate-fadeInUp animation-delay-400">
              {credentials.map((cred, i) => (
                <div key={i} className="flex items-center gap-2 text-white/90">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span className="text-sm font-medium">{cred}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-neutral-900 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">55+</div>
              <div className="text-neutral-400 text-sm">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">5000+</div>
              <div className="text-neutral-400 text-sm">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">15+</div>
              <div className="text-neutral-400 text-sm">Cities Served</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">100%</div>
              <div className="text-neutral-400 text-sm">Satisfaction Guaranteed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-4">
              Residential Contracting Services
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto text-lg">
              Comprehensive home improvement solutions across the Dallas-Fort Worth metroplex
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesList.map((service) => (
              <Link 
                key={service.id} 
                to={`/services/${service.id}`}
                className="service-card group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={serviceImages[service.id]?.image} 
                    alt={service.title}
                    className="service-image w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 to-transparent" />
                  <h3 className="absolute bottom-4 left-4 right-4 text-white font-semibold text-lg">
                    {service.shortTitle}
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                    {serviceDescriptions[service.id]}
                  </p>
                  <span className="text-amber-600 font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Why Gray Contracting</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-6">
                Dallas's Most Trusted Home Improvement Contractor
              </h2>
              <p className="text-neutral-600 text-lg mb-8">
                For over 55 years, Gray Contracting has been the go-to choice for homeowners across 
                Dallas-Fort Worth seeking quality craftsmanship, reliable service, and exceptional results.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">55+ Years of Excellence</h3>
                    <p className="text-neutral-600 text-sm">Five decades of trusted craftsmanship.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Family Owned</h3>
                    <p className="text-neutral-600 text-sm">Personalized service and accountability.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Licensed & Insured</h3>
                    <p className="text-neutral-600 text-sm">Full licensing for your peace of mind.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Free Estimates</h3>
                    <p className="text-neutral-600 text-sm">Transparent pricing, no hidden fees.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80" 
                alt="Professional home improvement craftsmanship"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-500 text-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">55+</div>
                <div className="text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Service Areas</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              Proudly Serving the Dallas-Fort Worth Metroplex
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, i) => {
              const slug = area.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link 
                  key={i}
                  to={`/services/residential-painting/${slug}`}
                  className="px-6 py-3 bg-neutral-800 hover:bg-amber-500 rounded-full text-sm font-medium transition-colors"
                >
                  {area}, TX
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mold Remediation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Mold Remediation</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-6">
                Concerned About Mold or Moisture Damage?
              </h2>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                If you have visible mold, a persistent musty odor, or moisture damage after a leak, Gray Contracting can help. We provide professional mold testing, remediation, and can repair the drywall, paint, and building materials affected by the project.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Mold testing and inspection",
                  "Professional containment and remediation",
                  "Black mold and attic mold removal",
                  "Moisture source correction",
                  "Post-remediation reconstruction"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-500" />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services/mold-remediation">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6">
                    Learn About Mold Remediation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+12149087644">
                  <Button variant="outline" className="border-neutral-300">
                    <Phone className="mr-2 w-4 h-4" />
                    Call 214-908-7644
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80" 
                alt="Professional mold remediation services"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-4 -right-4 bg-neutral-900 text-white p-5 rounded-xl shadow-lg">
                <div className="text-sm font-medium text-amber-400">Fully Insured</div>
                <div className="text-xs text-neutral-400">Testing & Remediation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Client Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4">
              What Our Customers Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((j) => (
                  <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-6 italic">"Gray Contracting transformed our kitchen beyond what we imagined!"</p>
              <div className="font-semibold text-neutral-900">Sarah M.</div>
              <div className="text-sm text-neutral-500">Dallas, TX</div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((j) => (
                  <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-6 italic">"Professional, on-time, and the quality of their painting work is outstanding!"</p>
              <div className="font-semibold text-neutral-900">Robert J.</div>
              <div className="text-sm text-neutral-500">Plano, TX</div>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map((j) => (
                  <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-6 italic">"They handled our mold issue quickly. Our home feels safe again."</p>
              <div className="font-semibold text-neutral-900">Jennifer L.</div>
              <div className="text-sm text-neutral-500">Frisco, TX</div>
            </div>
          </div>
        </div>
      </section>

      {/* From Our Blog Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Expert Advice</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4">From Our Blog</h2>
            <p className="text-neutral-600 mt-3 max-w-2xl mx-auto">Expert guides and tips for Dallas-Fort Worth homeowners from our 55+ years of experience.</p>
          </div>
          <HomeBlogCards />
          <div className="text-center mt-8">
            <Link to="/blog">
              <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                View All Articles <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Home?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Get a free, no-obligation estimate from Dallas's most trusted contractor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">What services does Gray Contracting offer?</h3>
              <p className="text-neutral-600">We offer painting, remodeling, drywall repair, carpentry, tile installation, siding, deck construction, and mold remediation throughout Dallas-Fort Worth.</p>
            </div>
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">How long has Gray Contracting been in business?</h3>
              <p className="text-neutral-600">Gray Contracting is a family-owned business with over 55 years of experience serving Dallas-Fort Worth since 1969.</p>
            </div>
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">Do you offer free estimates?</h3>
              <p className="text-neutral-600">Yes! We provide free, no-obligation estimates for all residential projects. Contact us to schedule your consultation.</p>
            </div>
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">Is Gray Contracting licensed and insured?</h3>
              <p className="text-neutral-600">Absolutely. We are fully licensed and insured, including specialized certifications for lead and mold remediation.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
